import { useQueryClient } from '@tanstack/react-query';
import { useAxiosInfiniteQuery, useAxiosMutation, useAxiosQuery } from './use-axios.hook';
import { UpdateArcgisCivil3dAlignmentSettingApiRequest } from '../../../../types/requests/arcgis-civil3d-settings/alignments/update-arcgis-civil3d-alignment-setting.request';
import { CreateArcgisCivil3dAlignmentSettingApiRequest } from '../../../../types/requests/arcgis-civil3d-settings/alignments/create-arcgis-civil3d-alignment-setting.request';
import {
  updateAlignmentSetting,
  createAlignmentSetting,
  createPropertyMappingForAlignment,
  getAlignmentSetting,
  deleteAlignmentSettings,
  deletePropertyMappingFromAlignment,
  getAllPropertyMappingsForAlignment,
  updatePropertyMappingsForAlignment,
  duplicateAlignmentSettings,
  createPropertyMappingsForAlignments,
  searchPropertyMappingsForAlignments,
} from '../../api/arcgis-civil3d-alignment-setting.api';
import { ArcgisCivil3dAlignmentSettingApiModel } from '../../../../types/models/arcgis-civil3d-alignment-setting.model';
import { GetArcgisCivil3dAlignmentSettingApiRequest } from '../../../../types/requests/arcgis-civil3d-settings/alignments/get-arcgis-civil3d-alignment-setting.request';
import { DeleteArcgisCivil3dAlignmentSettingsApiRequest } from '../../../../types/requests/arcgis-civil3d-settings/alignments/delete-arcgis-civil3d-alignment-settings.request';
import { CreatePropertyMappingForAlignmentApiRequest } from '../../../../types/requests/arcgis-civil3d-settings/alignments/create-property-mapping-for-alignment.request';
import { DeletePropertyMappingForAlignmentApiRequest } from '../../../../types/requests/arcgis-civil3d-settings/alignments/delete-property-mapping-for-alignment.request';
import { GetAllPropertyMappingsForAlignmentApiRequest } from '../../../../types/requests/arcgis-civil3d-settings/alignments/get-all-property-mappings-for-alignment.request';
import { UpdatePropertyMappingsForAlignmentApiRequest } from '../../../../types/requests/arcgis-civil3d-settings/alignments/update-property-mappings-for-alignment.request';
import { DuplicateArcgisCivil3dAlignmentsApiRequest } from '../../../../types/requests/arcgis-civil3d-settings/alignments/duplicate-arcgis-civil3d-alignment-settings.request';
import { CreatePropertyMappingsForAlignmentsApiRequest } from '../../../../types/requests/arcgis-civil3d-settings/alignments/create-property-mappings-for-alignments.request';
import { SearchPropertyMappingsForAlignmentsApiRequest } from '../../../../types/requests/arcgis-civil3d-settings/alignments/search-property-mappings-for-alignments.request';

const baseQueryKey = 'alignment-setting';
const searchQueryKey = 'search';
const propertyMappingQueryKey = 'property-mappings';

export const useUpdateAlignmentSetting = () => {
  const queryClient = useQueryClient();
  const [request, { data, ...result }] = useAxiosMutation({
    mutationFn: async (params: UpdateArcgisCivil3dAlignmentSettingApiRequest) =>
      (await updateAlignmentSetting(params)).data,
    onSuccess: (alignment) =>
      queryClient.invalidateQueries({
        predicate: ({ queryKey }) =>
          queryKey[0] === baseQueryKey &&
          (queryKey[1] === 'search' ||
            (queryKey[1] as UpdateArcgisCivil3dAlignmentSettingApiRequest)?.configurationId ===
              alignment.configuration?.id),
      }),
  });
  return [request, { ...result, alignment: data }] as const;
};

export const useCreateAlignmentSetting = () => {
  const queryClient = useQueryClient();
  const [request, { data, ...result }] = useAxiosMutation({
    mutationFn: async (params: CreateArcgisCivil3dAlignmentSettingApiRequest) =>
      (await createAlignmentSetting(params)).data,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [baseQueryKey, searchQueryKey] }),
  });
  return [request, { ...result, alignment: data }] as const;
};

export const useGetAlignmentSetting = <T extends ArcgisCivil3dAlignmentSettingApiModel>(
  params: GetArcgisCivil3dAlignmentSettingApiRequest,
) => {
  const { data, ...result } = useAxiosQuery<T>({
    queryKey: [baseQueryKey, params],
    queryFn: async () => (await getAlignmentSetting<T>(params)).data,
    enabled: Boolean(params.alignmentId && !Number.isNaN(params.alignmentId)),
  });
  return { ...result, alignment: data } as const;
};

export const useDuplicateAlignmentSettings = () => {
  const queryClient = useQueryClient();
  const [request, { data, ...result }] = useAxiosMutation({
    mutationFn: async (params: DuplicateArcgisCivil3dAlignmentsApiRequest) =>
      (await duplicateAlignmentSettings(params)).data,
    onSuccess: () =>
      queryClient.invalidateQueries({
        predicate: ({ queryKey }) => queryKey[0] === baseQueryKey && queryKey[1] === searchQueryKey,
      }),
  });
  return [request, result] as const;
};

export const useDeleteAlignmentSettings = () => {
  const queryClient = useQueryClient();
  const [request, { data, ...result }] = useAxiosMutation({
    mutationFn: async (params: DeleteArcgisCivil3dAlignmentSettingsApiRequest) =>
      (await deleteAlignmentSettings(params)).data,
    onSuccess: () =>
      queryClient.invalidateQueries({
        predicate: ({ queryKey }) => queryKey[0] === baseQueryKey && queryKey[1] === searchQueryKey,
      }),
  });
  return [request, result] as const;
};

export const useCreatePropertyMappingForAlignment = () => {
  const queryClient = useQueryClient();
  const [request, { data, ...result }] = useAxiosMutation({
    mutationFn: async (params: CreatePropertyMappingForAlignmentApiRequest) =>
      (await createPropertyMappingForAlignment(params)).data,
    onSuccess: () =>
      queryClient.invalidateQueries({
        predicate: ({ queryKey }) => queryKey[0] === baseQueryKey && queryKey[1] === propertyMappingQueryKey,
      }),
  });
  return [request, { ...result, ...data }] as const;
};

export const useCreatePropertyMappingsForAlignments = () => {
  const queryClient = useQueryClient();
  const [request, { data, ...result }] = useAxiosMutation({
    mutationFn: async (params: CreatePropertyMappingsForAlignmentsApiRequest) =>
      (await createPropertyMappingsForAlignments(params)).data,
    onSuccess: () =>
      queryClient.invalidateQueries({
        predicate: ({ queryKey }) => queryKey[0] === baseQueryKey && queryKey[1] === searchQueryKey,
      }),
  });
  return [request, { ...result, ...data }] as const;
};

export const useSearchPropertyMappingsForAlignments = (params: SearchPropertyMappingsForAlignmentsApiRequest) => {
  const { data, ...result } = useAxiosInfiniteQuery({
    queryKey: [baseQueryKey, searchQueryKey, params],
    queryFn: async ({ pageParam }) =>
      (await searchPropertyMappingsForAlignments({ cursor: pageParam, ...params })).data,
    initialPageParam: params.cursor,
    getNextPageParam: (lastResponse) => lastResponse.nextCursor,
  });
  return { ...result, response: data } as const;
};

export const useUpdatePropertyMappingsForAlignment = () => {
  const queryClient = useQueryClient();
  const [request, { data, ...result }] = useAxiosMutation({
    mutationFn: async (params: UpdatePropertyMappingsForAlignmentApiRequest) =>
      (await updatePropertyMappingsForAlignment(params)).data,
    onSuccess: () =>
      queryClient.invalidateQueries({
        predicate: ({ queryKey }) => queryKey[0] === baseQueryKey && queryKey[1] === propertyMappingQueryKey,
      }),
  });
  return [request, { ...result, ...data }] as const;
};

export const useGetAllPropertyMappingsForAlignment = (params: GetAllPropertyMappingsForAlignmentApiRequest) => {
  const { data, ...result } = useAxiosQuery({
    queryKey: [baseQueryKey, params],
    queryFn: async () => (await getAllPropertyMappingsForAlignment(params)).data,
    enabled: Boolean(params.alignmentId && !Number.isNaN(params.alignmentId)),
  });
  return { ...result, ...data } as const;
};

export const useDeletePropertyMappingFromAlignment = () => {
  const queryClient = useQueryClient();
  const [request, { data, ...result }] = useAxiosMutation({
    mutationFn: async (params: DeletePropertyMappingForAlignmentApiRequest) =>
      (await deletePropertyMappingFromAlignment(params)).data,
    onSuccess: () =>
      queryClient.invalidateQueries({
        predicate: ({ queryKey }) => queryKey[0] === baseQueryKey && queryKey[1] === propertyMappingQueryKey,
      }),
  });
  return [request, result] as const;
};
